
  import GFiles from '@/components/core/files/GFiles.vue'
  import AutoResume from '@/components/forms/AutoResume.vue'
  import FormTitle from '@/components/forms/FormTitle.vue'
  import { FileParameter, FileProcess } from '@/entities/files'
  import { PurchaseOrder } from '@/entities/purchase'
  import { SaleOrder } from '@/entities/sales'
  import { Process } from '@/entities/settings'
  import { plainToInstance } from 'class-transformer'
  import { Component } from 'vue-property-decorator'
  import { mapActions } from 'vuex'
  import Cell from './cell'
  import { insertInput, updateInput } from '@/graphql/generated-types'
  import dayjs from 'dayjs'
  import { utcToLocal } from '@/utils/general'
  import { Person } from '@/entities/persons'
  import GRadioButton from '@/components/core/input/GRadioButton.vue'

@Component({
  components: {
    FormTitle,
    AutoResume,
    GFiles,
    GRadioButton,
  },
  methods: {
    ...mapActions('resources/form', ['pushData', 'deleteFile']),
  },
})
  export default class DeliveryPhotoCell extends Cell {
    pushData!: (payload: {
      model: string
      fields?: insertInput | updateInput
    }) => Promise<any>;

    deleteFile!: (
      payload: Array<{ id: number, idFileProcess: number }>
    ) => Promise<void>;

    itemProcess: Process = plainToInstance(Process, {})
    fileParameter: FileParameter = plainToInstance(FileParameter, {})
    acquirers: Person[] = []
    displayDialog: boolean = false
    loading: boolean = false
    disableForm: boolean = true
    photo = []

    formData = {
      valid: null,
      comment: '',
    }

    async mounted () {
      const { isSale, isPurchase } = this

      let tableName
      if (isSale) {
        tableName = 'sale_order'

        this.acquirers = (await this.fetchData({
          query: { name: 'find', model: 'SaleOrder' },
          filter: { id: { _eq: this.item.id } },
        }))[0]
          ?.document
          ?.interveners
          ?.filter(intervener => intervener.field.name === 'buyer')
          ?.map(intervener => intervener.person)
      } else if (isPurchase) {
        tableName = 'purchase_order'

        const document = (await this.fetchData({
          query: { name: 'find', model: 'Document' },
          filter: { id_process_record: { _eq: this.item.id } },
        }))[0]

        this.acquirers = document
          ?.interveners
          ?.filter(intervener => intervener.field.name === 'seller')
          ?.map(intervener => intervener.person)
      }

      this.itemProcess = (await this.fetchData({
        query: { name: 'find', model: 'Process' },
        filter: { table_name: { _eq: tableName } },
      }))[0]

      this.fileParameter = ((await this.fetchData({
        query: { name: 'find', model: 'FileParameter' },
        filter: {
          _and: [
            { name: { _eq: 'delivery_photo' } },
            { id_process: { _eq: this.itemProcess.id } },
          ],
        },
      })))[0]

      this.photo = await this.fetchData({
        query: { name: 'find', model: 'FileProcess' },
        filter: {
          _and: [
            { id_process_record: { _eq: this.item.id } },
            { id_file_parameter: { _eq: this.fileParameter.id } },
          ],
        },
        force: true,
      })

      if (this.photo?.length) {
        const lastPhotoDate = this.photo[0].createdAt

        this.disableForm = dayjs().diff(utcToLocal(lastPhotoDate), 'hour') > 48 || this.photo[0].valid

        this.formData.comment = this.photo[0].comment || ''
        this.formData.valid = this.photo[0].valid
      } else {
        this.disableForm = false
      }
    }

    async send () {
      const { formData, isSupervisor } = this
      if (!this.photo.length) return

      this.loading = true

      if (isSupervisor) {
        const idFileProcess = this.photo[0].id
        await this.pushData({
          model: 'FileProcess',
          fields: {
            id: idFileProcess,
            valid: formData.valid,
            comment: formData.valid ? null : formData.comment,
            validation_type: 'manual',
          },
        })

        this.photo[0].valid = formData.valid
        this.photo[0].comment = formData.comment
        this.photo[0].validationType = 'manual'
      } else {
        await this.handleFileType(this.photo, { properties: this.photoProperties }, this.process.id, this.item.id)
      }
      this.loading = false
      this.toggleDialog()
    }

    async handleFileType (fileType, field, idProcess, id, fileParameter = null) {
      const fileFiltered = fileType.filter(_ => _)

      const isArrayFiles = fileFiltered.some(val => Boolean(val?.src))
      if (fileFiltered?.length) {
        const parameter = fileParameter || await this.fetchData({
          query: { name: 'find', model: 'FileParameter' },
          filter: {
            _and: [
              { id_process: { _eq: idProcess } },
              { name: { _eq: field.properties.name } },
            ],
          },
        })
        const files = isArrayFiles
          ? fileFiltered.map(fi => fi.id)
          : fileFiltered.map(fi => fi.file.id)
        const allow = await this.allowFile(files, id, parameter[0].id)
        if (!allow?.length) return

        const resp = await Promise.all(
          allow.map(item =>
            this.pushData({
              model: 'FileProcess',
              fields: {
                id_file: item,
                id_file_parameter: parameter[0].id,
                id_process_record: id,
              },
            })
          )
        )

        if (!field?.properties?.multiple) {
          await this.removeOldFile((resp as FileProcess[]).map(_ => _.id), parameter[0].id, id)
        }

        return resp
      }
    }

    async removeOldFile (newFileId, idParameter, id) {
      const findFiles = await this.fetchData({
        query: { name: 'find', model: 'FileProcess' },
        filter: {
          _and: [
            { id: { _nin: newFileId } },
            { id_process_record: { _eq: id } },
            { id_file_parameter: { _eq: idParameter } },
          ],
        },
      })

      if (findFiles?.length) {
        const files = findFiles.map(_ => {
          return {
            id: _.file.id,
            idFileProcess: _.id,
          }
        })

        await this.deleteFile(files)
      }
    }

    async allowFile (files, id, parameterId) {
      if (!files?.length) return []

      const process = await this.fetchData({
        query: { name: 'find', model: 'FileProcess' },
        filter: {
          _and: [{ id_file: { _in: files } }, { id_process_record: { _eq: id } }, { id_file_parameter: { _eq: parameterId } }],
        },
        force: true,
      })

      return files.filter(
        file => !process.some(fileProcess => fileProcess.file.id === file)
      )
    }

    toggleDialog () {
      this.displayDialog = !this.displayDialog
    }

    get photoProperties () {
      const { fileParameter } = this
      const { fileType } = fileParameter

      if (!fileType) return
      return {
        accept: fileType.mimes,
        multiple: fileParameter.multiple,
        fileTypeId: fileType.id,
        name: fileParameter.name,
        label: fileParameter.description,
        required: fileParameter.required,
        icon: fileType.icon,
      }
    }

    get isSale () {
      return this.item instanceof SaleOrder
    }

    get isPurchase () {
      return this.item instanceof PurchaseOrder
    }

    get deal () {
      const { isSale, isPurchase } = this

      if (isSale) {
        return this.item.deal
      }
      if (isPurchase) {
        return this.item?.negotiation?.inspection?.appraisal?.deal
      }
    }

    get stock () {
      const { deal } = this

      return deal.stock
    }

    get client () {
      const { deal } = this

      return deal.lead.client
    }

    get disableButton () {
      const { hasPhoto, isSupervisor } = this

      return isSupervisor && !hasPhoto
    }

    get hasPhoto () {
      const { photo } = this

      return Boolean(photo?.length)
    }

    get icon () {
      const { isSupervisor, hasPhoto, disableForm } = this

      if (isSupervisor) {
        return 'mdi-camera'
      } else {
        return hasPhoto && disableForm ? 'mdi-eye' : 'mdi-camera'
      }
    }

    get color () {
      const { hasPhoto, photo } = this

      if (hasPhoto) {
        if (!photo[0].validationType) {
          return 'primary' // Azul
        }
        return photo[0].valid ? '#13CE66' : 'red' // Verde : Rojo
      }
      return '#FF9800' // Amarillo
    }

    get isSupervisor () {
      const { $route } = this

      return $route.fullPath.includes('supervisor')
    }
  }
